import type { ReactElement } from 'react';
import {
  PlatformBadge,
  getIdeClientDisplayLabel,
} from '@/components/shared/platform';
import { type CoverageStatus } from '@kirin/api-client/contracts/user-security-posture';
import type { LoggedInUserMatch } from '@kirin/api-client/contracts/shadow-ai';
import {
  buildLoggedInTooltip,
  isLoggedInUserMismatch,
  loggedInMatchChipClass,
  loggedInMatchBorderClass,
} from './client-utils';

interface ClientBadgeProps {
  client: string;
  coverageStatus?: CoverageStatus;
  lastActiveAt?: string | null;
  displayName?: string | null;
  showTooltip?: boolean;
  /** KIR-2916: account signed in to this client + its match to the user. */
  loggedInUserEmail?: string | null;
  loggedInUserMatch?: LoggedInUserMatch;
}

/**
 * Badge component for displaying a single IDE client with coverage-aware tooltip.
 */
export function ClientBadge({
  client,
  coverageStatus,
  lastActiveAt,
  displayName,
  showTooltip = true,
  loggedInUserEmail,
  loggedInUserMatch,
}: ClientBadgeProps): ReactElement {
  const mismatch = isLoggedInUserMismatch(loggedInUserEmail, loggedInUserMatch);
  const tooltipText = buildLoggedInTooltip(
    coverageStatus,
    lastActiveAt,
    loggedInUserMatch,
    loggedInUserEmail
  );
  const displayLabel = getIdeClientDisplayLabel(client, displayName);

  return (
    <PlatformBadge
      platform={client}
      label={displayLabel}
      tooltipText={tooltipText}
      showTooltip={showTooltip}
      className={
        mismatch
          ? `${loggedInMatchChipClass(loggedInUserMatch)} ${loggedInMatchBorderClass(loggedInUserMatch)}`
          : undefined
      }
    />
  );
}
